/**
 * ContrattoForm Component - Hedita Design System
 * Feature: Legal - Form creazione/modifica contratti
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, ArrowLeft, CheckCircle, AlertCircle, Save } from "lucide-react";
import { useContratto, useCreateContratto, useUpdateContratto } from "../hooks";
import { useGuests } from "@/features/ospiti";
import { useStrutture } from "@/features/strutture";
import { useQueryClient } from "@tanstack/react-query";

const TIPI_CONTRATTO = ["Affitto", "Subaffitto", "Uso", "Comodato", "Altro"];
const STATI_CONTRATTO = ["Bozza", "Firmato", "Attivo", "Scaduto", "Annullato"];

const empty = {
  titolo: "",
  tipo: "Affitto",
  stato: "Bozza",
  data_inizio: new Date().toISOString().split('T')[0],
  data_fine: "",
  ospite_id: "",
  struttura_id: "",
  importo: 0,
  note: "",
};

interface ContrattoFormProps {
  id?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSaved?: () => void;
}

export function ContrattoForm({ id, onSaved }: ContrattoFormProps) {
  const isNew = !id || id === "nuovo";
  const navigate = useNavigate();
  
  const { data: contratto, isLoading } = useContratto(isNew ? undefined : id);
  const { data: ospiti = [] } = useGuests();
  const { data: strutture = [] } = useStrutture();
  const createMut = useCreateContratto();
  const updateMut = useUpdateContratto();

  const [form, setForm] = useState(empty);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (contratto) {
      setForm({
        titolo: contratto.titolo || "",
        tipo: contratto.tipo || "Affitto",
        stato: contratto.stato || "Bozza",
        data_inizio: contratto.data_inizio || new Date().toISOString().split('T')[0],
        data_fine: contratto.data_fine || "",
        ospite_id: contratto.ospite_id || "",
        struttura_id: contratto.struttura_id || "",
        importo: contratto.importo || 0,
        note: contratto.note || "",
      });
    }
  }, [contratto]);

  const set = (k: keyof typeof form, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.titolo.trim()) return;
    
    setSaveStatus("saving");
    setSaveError(null);

    try {
      if (isNew) {
        await createMut.mutateAsync(form);
      } else {
        await updateMut.mutateAsync({ id: id!, data: form });
      }
      
      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
        onSaved?.();
        navigate("/contratti");
      }, 1000);
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Errore durante il salvataggio");
    }
  };

  const isLoadingData = isLoading && !isNew;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/contratti")}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  {isNew ? "Nuovo Contratto" : "Modifica Contratto"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isNew ? "Crea un nuovo contratto" : "Modifica il contratto esistente"}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/contratti")}
                className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={!form.titolo.trim() || saveStatus === "saving"}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saveStatus === "saving" ? (
                  <>
                    <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    Salvataggio...
                  </>
                ) : saveStatus === "saved" ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Salvato
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Salva
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {isLoadingData ? (
          <div className="space-y-6">
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-10 bg-muted rounded-xl" />
            </div>
            <div className="animate-pulse">
              <div className="h-4 bg-muted rounded w-1/4 mb-2" />
              <div className="h-24 bg-muted rounded-xl" />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Informazioni generali */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-500" />
                Informazioni generali
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Titolo *
                  </label>
                  <input
                    type="text"
                    value={form.titolo}
                    onChange={(e) => set("titolo", e.target.value)}
                    placeholder="Titolo del contratto"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Tipo
                    </label>
                    <select
                      value={form.tipo}
                      onChange={(e) => set("tipo", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                    >
                      {TIPI_CONTRATTO.map(tipo => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Stato
                    </label>
                    <select
                      value={form.stato}
                      onChange={(e) => set("stato", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                    >
                      {STATI_CONTRATTO.map(stato => (
                        <option key={stato} value={stato}>{stato}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Date e importo */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Date e importo</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data inizio
                  </label>
                  <input
                    type="date"
                    value={form.data_inizio}
                    onChange={(e) => set("data_inizio", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data fine
                  </label>
                  <input
                    type="date"
                    value={form.data_fine}
                    onChange={(e) => set("data_fine", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Importo (€)
                  </label>
                  <input
                    type="number"
                    value={form.importo}
                    onChange={(e) => set("importo", parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Riferimenti */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Riferimenti</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Ospite
                  </label>
                  <select
                    value={form.ospite_id}
                    onChange={(e) => set("ospite_id", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                  >
                    <option value="">Seleziona un ospite</option>
                    {ospiti.map(ospite => (
                      <option key={ospite.id} value={ospite.id}>
                        {ospite.nome} {ospite.cognome}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Struttura
                  </label>
                  <select
                    value={form.struttura_id}
                    onChange={(e) => set("struttura_id", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all appearance-none"
                  >
                    <option value="">Seleziona una struttura</option>
                    {strutture.map(struttura => (
                      <option key={struttura.id} value={struttura.id}>
                        {struttura.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Note</h2>
              
              <textarea
                value={form.note}
                onChange={(e) => set("note", e.target.value)}
                placeholder="Note aggiuntive sul contratto"
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
              />
            </div>

            {/* Error */}
            {saveError && (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-destructive/10 border border-destructive/20">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">{saveError}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
